import React, { useRef, useState } from 'react';
import { Download, Upload, History, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { downloadBlob, filenameFromHeaders, formatFechaHora, errorMsg } from '@/lib/recuentos';

const productosExcelAPI = {
  exportar: () => api.get('/productos-excel/exportar', { responseType: 'blob' }),
  preview: (file) => { const fd = new FormData(); fd.append('file', file); return api.post('/productos-excel/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); },
  aplicar: (id) => api.post(`/productos-excel/aplicar/${id}`),
  respaldos: () => api.get('/productos-excel/respaldos'),
  respaldoExcel: (id) => api.get(`/productos-excel/respaldos/${id}/excel`, { responseType: 'blob' }),
};

const download = async (promise, fallback) => {
  try { const res = await promise; downloadBlob(res.data, filenameFromHeaders(res.headers, fallback)); }
  catch { toast.error('Error al descargar'); }
};

const Stat = ({ label, value, cls, testId }) => (
  <div className={`rounded-lg px-3 py-2 ${cls}`} data-testid={testId}><div className="text-2xl font-bold">{value}</div><div className="text-xs">{label}</div></div>
);

const Cambio = ({ c }) => (
  <div className="border rounded-lg p-2.5 text-sm" data-testid={`prod-cambio-${c.codigo}`}>
    <div className="flex items-center gap-2 mb-1">
      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${c.accion === 'crear' ? 'bg-blue-100 text-blue-700' : c.desactiva ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'}`}>
        {c.accion === 'crear' ? 'Nuevo' : c.desactiva ? 'Se desactiva' : 'Actualiza'}
      </span>
      <span className="font-mono text-xs text-slate-500">{c.codigo}</span><span className="font-medium text-slate-800">{c.nombre}</span>
      <span className="ml-auto text-[11px] text-slate-400">fila {c.fila}</span>
    </div>
    <ul className="space-y-0.5 text-xs">
      {c.campos.map((f) => (
        <li key={f.campo}><span className="text-slate-500">{f.campo}:</span>{' '}
          {c.accion === 'crear' ? <b>{String(f.despues)}</b> : <><span className="line-through text-red-600">{String(f.antes)}</span> → <b className="text-green-700">{String(f.despues)}</b></>}
        </li>
      ))}
    </ul>
  </div>
);

const Errores = ({ errores }) => (
  <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm" data-testid="prod-preview-errores">
    <p className="font-semibold text-red-700 mb-1 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> {errores.length} filas con error (se omitirán):</p>
    <ul className="space-y-0.5 text-red-700 text-xs">{errores.map((e) => <li key={e.fila}>Fila {e.fila} · {e.codigo || 'sin código'}: {e.errores.join('; ')}</li>)}</ul>
  </div>
);

const Respaldos = ({ list }) => (
  <div className="space-y-1 text-sm" data-testid="prod-respaldos-list">
    {list.length === 0 && <p className="text-slate-500">Aún no hay respaldos.</p>}
    {list.map((r) => (
      <div key={r.id} className="flex items-center gap-2 border rounded-lg px-3 py-2">
        <div className="flex-1"><div>{formatFechaHora(r.created_at)} · {r.user_name}</div><div className="text-xs text-slate-500">{r.motivo} · {r.total_items} productos</div></div>
        <Button size="sm" variant="outline" onClick={() => download(productosExcelAPI.respaldoExcel(r.id), 'respaldo.xlsx')} data-testid={`prod-respaldo-dl-${r.id}`}><Download className="w-4 h-4" /></Button>
      </div>
    ))}
  </div>
);

export const ProductosExcelActions = ({ onApplied }) => {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [respaldos, setRespaldos] = useState(null);
  const [busy, setBusy] = useState(false);

  const subir = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setBusy(true);
    try { setPreview((await productosExcelAPI.preview(file)).data); }
    catch (err) { toast.error(errorMsg(err, 'Error al leer el archivo')); }
    finally { setBusy(false); }
  };

  const confirmar = async () => {
    setBusy(true);
    try {
      const { data } = await productosExcelAPI.aplicar(preview.preview_id);
      toast.success(`Listo: ${data.actualizados} actualizados, ${data.creados} creados. Respaldo guardado.`);
      setPreview(null);
      onApplied && onApplied();
    } catch (err) { toast.error(errorMsg(err, 'Error al aplicar')); }
    finally { setBusy(false); }
  };

  const abrirRespaldos = async () => setRespaldos((await productosExcelAPI.respaldos()).data);
  const r = preview?.resumen;
  const total = r ? r.actualizar + r.crear : 0;

  return (
    <>
      <Button variant="outline" onClick={() => download(productosExcelAPI.exportar(), 'productos_tupu.xlsx')} data-testid="export-products-btn">
        <Download className="w-4 h-4 mr-2" /> Exportar productos (Excel)
      </Button>
      <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={subir} data-testid="update-products-input" />
      <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="update-products-btn">
        <Upload className="w-4 h-4 mr-2" /> {busy && !preview ? 'Leyendo...' : 'Actualizar desde Excel'}
      </Button>
      <Button variant="ghost" onClick={abrirRespaldos} title="Respaldos" data-testid="products-backups-btn"><History className="w-4 h-4" /></Button>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="prod-preview-dialog">
          <DialogHeader><DialogTitle>Vista previa de la actualización</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-3 overflow-y-auto min-h-0 pr-1">
              <div className="grid grid-cols-5 gap-2 text-center">
                <Stat label="se actualizan" value={r.actualizar} cls="bg-amber-50 text-amber-800" testId="prod-stat-actualizar" />
                <Stat label="se crean" value={r.crear} cls="bg-blue-50 text-blue-700" testId="prod-stat-crear" />
                <Stat label="se desactivan" value={r.desactivar} cls="bg-red-50 text-red-700" testId="prod-stat-desactivar" />
                <Stat label="sin cambios" value={r.sin_cambios} cls="bg-slate-50 text-slate-600" testId="prod-stat-sin-cambios" />
                <Stat label="con error" value={r.errores} cls="bg-red-50 text-red-700" testId="prod-stat-errores" />
              </div>
              <p className="text-xs text-slate-500">El stock actual no se modifica (los nuevos se crean con stock 0). Ningún producto se borra. Antes de aplicar se guarda un respaldo automático.</p>
              {preview.errores.length > 0 && <Errores errores={preview.errores} />}
              {preview.cambios.map((c) => <Cambio key={c.fila} c={c} />)}
              {total === 0 && <p className="text-sm text-slate-600 flex items-center gap-1"><CheckCircle2 className="w-4 h-4 text-green-600" /> No hay cambios para aplicar.</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreview(null)}>Cancelar</Button>
            <Button onClick={confirmar} disabled={busy || !total} className="bg-orange-500 hover:bg-orange-600" data-testid="prod-confirmar-btn">
              {busy ? 'Aplicando...' : `Confirmar ${total} cambios`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!respaldos} onOpenChange={(o) => !o && setRespaldos(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="prod-respaldos-dialog">
          <DialogHeader><DialogTitle>Respaldos de productos</DialogTitle></DialogHeader>
          {respaldos && <Respaldos list={respaldos} />}
        </DialogContent>
      </Dialog>
    </>
  );
};
